import { useState, useEffect, useRef, useCallback } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useLanguage } from "@/i18n/index";
import PatientLayout from "@/components/layout/PatientLayout";
import MessagingNoticeModal from "@/components/consent/MessagingNoticeModal";
import api from "@/api";
import { toast } from "sonner";
import { Send, MessageSquare, ChevronUp, Paperclip, FileText, Download, Trash2, HeartHandshake, CheckCircle2 } from "lucide-react";
import { useWebSocket } from "@/contexts/WebSocketContext";
import { getItems } from "@/utils/apiHelpers";

function getDateLabel(dateStr, language, t) {
  const d = new Date(dateStr);
  const today = new Date();
  const yesterday = new Date();
  yesterday.setDate(today.getDate() - 1);
  if (d.toDateString() === today.toDateString()) return t("messages.today");
  if (d.toDateString() === yesterday.toDateString()) return t("messages.yesterday");
  return d.toLocaleDateString(language === "ar" ? "ar-MA" : "fr-FR", { weekday: "long", day: "numeric", month: "long" });
}

function shouldShowDateSeparator(messages, index) {
  if (index === 0) return true;
  const prev = new Date(messages[index - 1].createdAt).toDateString();
  const curr = new Date(messages[index].createdAt).toDateString();
  return prev !== curr;
}

function dedupeMessages(list = []) {
  const seen = new Set();
  return list.filter((msg, index) => {
    const stableKey = msg?.id || `${msg?.senderId || "unknown"}-${msg?.createdAt || index}-${msg?.content || ""}`;
    if (seen.has(stableKey)) return false;
    seen.add(stableKey);
    return true;
  });
}

function getMessageFileUrl(file) {
  return file?.url || "";
}

function isImageFile(file) {
  return file?.mimeType?.startsWith("image/");
}

export default function PatientMessaging() {
  const { user } = useAuth();
  const { t, language } = useLanguage();
  const { on } = useWebSocket();
  const [threads, setThreads] = useState([]);
  const [selectedThread, setSelectedThread] = useState(null);
  const [messages, setMessages] = useState([]);
  const [hasMore, setHasMore] = useState(false);
  const [loadingOlder, setLoadingOlder] = useState(false);
  const [newMsg, setNewMsg] = useState("");
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [peerTyping, setPeerTyping] = useState(false);
  const [uploading, setUploading] = useState(false);
  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const typingTimeoutRef = useRef(null);
  const fileInputRef = useRef(null);
  const messageInputRef = useRef(null);
  const selectedThreadRef = useRef(null);

  const appendUniqueMessage = useCallback((message) => {
    if (!message) return;
    setMessages(prev => dedupeMessages([...prev, message]));
  }, []);

  useEffect(() => { fetchThreads(); }, []);

  useEffect(() => {
    selectedThreadRef.current = selectedThread;
  }, [selectedThread]);

  useEffect(() => {
    if (!on) return;
    const unsub1 = on("NEW_MESSAGE", (data) => {
      if (selectedThread && data.threadId === selectedThread.id) {
        const shouldKeepBottom = isNearBottom() || data.message?.senderId === user?.id;
        appendUniqueMessage(data.message);
        markRead(selectedThread.id);
        if (shouldKeepBottom) scrollToBottom("auto");
      } else {
        fetchThreads({ autoSelect: false });
      }
    });
    const unsub2 = on("TYPING", (data) => {
      if (selectedThread && data.threadId === selectedThread.id && data.userId !== user?.id) {
        setPeerTyping(true);
        clearTimeout(typingTimeoutRef.current);
        typingTimeoutRef.current = setTimeout(() => setPeerTyping(false), 3000);
      }
    });
    return () => { unsub1(); unsub2(); };
  }, [on, selectedThread, appendUniqueMessage, user?.id]);

  const isNearBottom = () => {
    const container = messagesContainerRef.current;
    if (!container) return true;
    return container.scrollHeight - container.scrollTop - container.clientHeight < 96;
  };

  const scrollToBottom = (behavior = "smooth") => {
    setTimeout(() => messagesEndRef.current?.scrollIntoView({ behavior, block: "end" }), 80);
  };

  const fetchThreads = async ({ autoSelect = true } = {}) => {
    try {
      const res = await api.get("/threads");
      const threadsList = getItems(res.data);
      setThreads(threadsList);
      if (autoSelect && threadsList.length > 0 && !selectedThreadRef.current) {
        selectThread(threadsList[0]);
      }
    } catch {
      toast.error(t("messages.loadError"));
    } finally {
      setLoading(false);
    }
  };

  const selectThread = async (thread, { scroll = true } = {}) => {
    setSelectedThread(thread);
    selectedThreadRef.current = thread;
    setPeerTyping(false);
    try {
      const res = await api.get(`/threads/${thread.id}/messages`, { skipAuthRedirect: true });
      const data = res.data;
      setMessages(dedupeMessages(data.messages || data));
      setHasMore(data.hasMore || false);
      await markRead(thread.id);
      fetchThreads({ autoSelect: false });
      if (scroll) scrollToBottom("auto");
    } catch { return; }
  };

  const loadOlderMessages = async () => {
    if (!hasMore || loadingOlder || messages.length === 0) return;
    setLoadingOlder(true);
    const container = messagesContainerRef.current;
    const oldHeight = container?.scrollHeight || 0;
    try {
      const firstId = messages[0]?.id;
      const res = await api.get(`/threads/${selectedThread.id}/messages?before=${firstId}&limit=50`, { skipAuthRedirect: true });
      const data = res.data;
      const older = data.messages || [];
      if (older.length > 0) {
        setMessages(prev => dedupeMessages([...older, ...prev]));
        setHasMore(data.hasMore || false);
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            if (container) container.scrollTop = container.scrollHeight - oldHeight;
          });
        });
      } else {
        setHasMore(false);
      }
    } catch { return; } finally {
      setLoadingOlder(false);
    }
  };

  const handleScroll = useCallback(() => {
    const container = messagesContainerRef.current;
    window.dispatchEvent(new CustomEvent("hana-patient-scroll", { detail: { scrollTop: container?.scrollTop || 0 } }));
    if (container && container.scrollTop < 60 && hasMore && !loadingOlder) {
      loadOlderMessages();
    }
  }, [hasMore, loadingOlder, loadOlderMessages]);

  const markRead = async (threadId) => {
    try { await api.patch(`/threads/${threadId}/read`, {}, { skipAuthRedirect: true }); } catch { return; }
  };

  const handleDeleteThread = async () => {
    if (!selectedThread) return;
    const ok = window.confirm(t("messages.removeConversationConfirm"));
    if (!ok) return;
    try {
      await api.delete(`/threads/${selectedThread.id}?scope=me`);
      toast.success(t("messages.conversationRemoved"));
      setSelectedThread(null);
      setMessages([]);
      fetchThreads({ autoSelect: false });
    } catch (err) {
      toast.error(err.response?.data?.detail || t("messages.error"));
    }
  };

  const startNewConversation = async () => {
    if (creating) return;
    setCreating(true);
    try {
      // Patient creates a thread with themselves — the backend will auto-add the assigned nurse
      const res = await api.post("/threads", { patientId: user?.id });
      fetchThreads({ autoSelect: false });
      selectThread(res.data);
      toast.success(t("messages.conversationCreated"));
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (detail) toast.error(detail);
    } finally {
      setCreating(false);
    }
  };

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!newMsg.trim() || !selectedThread) return;
    setSending(true);
    try {
      const res = await api.post(`/threads/${selectedThread.id}/messages`, { content: newMsg });
      appendUniqueMessage(res.data);
      setNewMsg("");
      scrollToBottom("smooth");
    } catch {
      toast.error(t("messages.sendError"));
    } finally {
      setSending(false);
    }
  };

  const intentOptions = [
    { key: "symptom", label: t("messages.intentSymptom"), template: t("messages.intentSymptomTemplate") },
    { key: "question", label: t("messages.intentQuestion"), template: t("messages.intentQuestionTemplate") },
    { key: "treatment", label: t("messages.intentTreatment"), template: t("messages.intentTreatmentTemplate") },
    { key: "document", label: t("messages.intentDocument"), template: t("messages.intentDocumentTemplate") },
  ];

  const applyIntentTemplate = (template) => {
    setNewMsg((current) => current.trim() ? `${current.trim()}\n${template}` : template);
    window.setTimeout(() => messageInputRef.current?.focus(), 0);
  };

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !selectedThread) return;
    if (file.size > 10 * 1024 * 1024) {
      toast.error(t("messages.fileTooLarge"));
      return;
    }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const res = await api.post(`/threads/${selectedThread.id}/messages/file`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      appendUniqueMessage(res.data);
      scrollToBottom("smooth");
      toast.success(t("messages.fileSent"));
    } catch (err) {
      toast.error(err.response?.data?.detail || t("messages.sendError"));
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  if (loading) {
    return (
      <PatientLayout>
        <div className="flex items-center justify-center h-48">
          <div className="w-8 h-8 border-4 border-emerald-600 border-t-transparent rounded-full animate-spin" />
        </div>
      </PatientLayout>
    );
  }

  return (
    <PatientLayout>
      <MessagingNoticeModal />
      <div className="flex flex-col animate-fade-in-up h-[calc(100dvh-8.75rem)] min-h-[34rem]">
        {/* Header */}
        <div className="mb-2 flex items-center justify-between gap-2.5">
          <div className="flex items-start gap-3 min-w-0">
            <div className="w-10 h-10 rounded-2xl bg-[#E9F0EE] text-[#2C5E53] flex items-center justify-center flex-shrink-0 shadow-sm">
              <HeartHandshake className="w-5 h-5" strokeWidth={1.7} />
            </div>
            <div className="min-w-0">
              <h1 className="text-[1.35rem] font-extrabold text-slate-800 tracking-normal leading-tight" data-testid="patient-messaging-title">{t("messages.title")}</h1>
              <p className="text-[13px] text-slate-500 leading-snug mt-0.5" data-testid="patient-messaging-subtitle">{t("messages.subtitle")}</p>
              <p className="mt-1 inline-flex items-center gap-1.5 rounded-full bg-[#E9F0EE] px-2.5 py-1 text-[11px] font-extrabold text-[#2C5E53]" data-testid="patient-team-availability-chip">
                <span className="h-2 w-2 rounded-full bg-[#2C5E53] animate-pulse" />
                {t("messages.teamAvailable")}
              </p>
            </div>
          </div>
          {/* New conversation button + delete current */}
          <div className="flex gap-1.5 flex-shrink-0">
            {selectedThread && (
              <button
                onClick={handleDeleteThread}
                data-testid="patient-delete-conversation-btn"
                className="h-10 w-10 bg-white border border-rose-100 text-rose-500 rounded-2xl hover:bg-rose-50 transition-all flex items-center justify-center shadow-sm"
                title={t("messages.removeConversation")}
              >
                <Trash2 className="w-4 h-4" />
              </button>
            )}
            <button
              onClick={startNewConversation}
              disabled={creating}
              data-testid="patient-new-conversation-btn"
              className="min-h-10 px-3.5 bg-[#2C5E53] text-white text-[13px] font-extrabold rounded-2xl hover:bg-[#244d44] disabled:opacity-50 transition-all duration-200 shadow-[0_10px_24px_rgb(44,94,83,0.18)]"
            >
              {creating ? "..." : t("messages.newMessage")}
            </button>
          </div>
        </div>

        {threads.length === 0 ? (
          <div className="flex-1 flex flex-col items-center justify-center text-center patient-card bg-white/90 px-5" data-testid="patient-messaging-empty-state">
            <div className="w-20 h-20 bg-[#E9F0EE] rounded-2xl flex items-center justify-center mb-4 shadow-sm">
              <MessageSquare className="w-9 h-9 text-[#2C5E53]" strokeWidth={1.5} />
            </div>
            <p className="text-slate-800 font-extrabold text-xl" data-testid="patient-messaging-empty-title">{t("messages.noMessages")}</p>
            <p className="text-base text-slate-500 mt-2 max-w-xs leading-relaxed" data-testid="patient-messaging-empty-hint">{t("messages.noMessagesHint")}</p>
            <div className="mt-4 w-full max-w-xs rounded-2xl bg-[#F3F1EC] border border-[#2C5E53]/10 px-4 py-3 text-left" data-testid="patient-team-availability-card">
              <div className="flex items-start gap-2.5">
                <CheckCircle2 className="w-4 h-4 text-[#2C5E53] mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-sm font-extrabold text-slate-800" data-testid="patient-team-availability-title">{t("messages.teamAvailabilityTitle")}</p>
                  <p className="text-xs text-slate-500 mt-0.5 leading-relaxed" data-testid="patient-team-availability-desc">{t("messages.teamAvailabilityDesc")}</p>
                </div>
              </div>
            </div>
            <button
              onClick={startNewConversation}
              disabled={creating}
              data-testid="patient-empty-start-conversation-btn"
              className="mt-5 px-6 py-3.5 bg-[#2C5E53] text-white rounded-2xl text-base font-extrabold hover:bg-[#244d44] transition-all shadow-[0_12px_28px_rgb(44,94,83,0.18)]"
            >
              {t("messages.startConversation")}
            </button>
          </div>
        ) : (
          <div className="flex flex-col flex-1 min-h-0 bg-white/92 rounded-2xl border border-[#2C5E53]/10 overflow-hidden shadow-[0_16px_42px_rgb(15,23,42,0.06)]">
            {/* Thread tabs if multiple */}
            {threads.length > 1 && (
              <div className="flex gap-2 p-3 border-b border-[#2C5E53]/10 overflow-x-auto bg-white/80">
                {threads.map(thread => (
                  <button
                    key={thread.id}
                    onClick={() => selectThread(thread)}
                    data-testid={`patient-thread-tab-${thread.id}`}
                    className={`flex-shrink-0 px-4 py-2 rounded-2xl text-sm font-bold transition-all ${
                      selectedThread?.id === thread.id
                        ? "bg-[#E9F0EE] text-[#2C5E53]"
                        : "text-slate-500 hover:bg-[#F3F1EC]"
                    }`}
                  >
                    {thread.patientName || t("messages.defaultThreadName")}
                    {thread.unreadCount > 0 && (
                      <span className="ml-1.5 bg-sky-500 text-white text-[10px] px-1 rounded-full">{thread.unreadCount}</span>
                    )}
                  </button>
                ))}
              </div>
            )}

            {/* Messages */}
            <div
              ref={messagesContainerRef}
              onScroll={handleScroll}
              className="patient-chat-shell flex-1 min-h-0 overflow-y-auto overscroll-contain p-3 space-y-1"
              data-testid="patient-messages-container"
            >
              {hasMore && (
                <button
                  onClick={loadOlderMessages}
                  disabled={loadingOlder}
                  data-testid="patient-load-older-messages-btn"
                  className="w-full py-3 text-sm text-slate-500 hover:text-[#2C5E53] flex items-center justify-center gap-1 transition-colors font-bold"
                >
                  {loadingOlder ? (
                    <div className="w-4 h-4 border-2 border-slate-300 border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <><ChevronUp className="w-3.5 h-3.5" /> {t("messages.olderMessages")}</>
                  )}
                </button>
              )}

              {messages.map((msg, idx) => {
                const isMe = msg.senderId === user?.id;
                const showDate = shouldShowDateSeparator(messages, idx);
                return (
                  <div key={msg.id || `${msg.senderId}-${msg.createdAt}-${idx}`}>
                    {showDate && (
                      <div className="flex items-center gap-3 my-4">
                        <div className="flex-1 h-px bg-slate-200" />
                        <span className="text-[12px] text-slate-500 font-bold px-2">{getDateLabel(msg.createdAt, language, t)}</span>
                        <div className="flex-1 h-px bg-slate-200" />
                      </div>
                    )}
                    <div
                      data-testid={`message-${msg.id}`}
                      className={`flex ${isMe ? "justify-end" : "justify-start"} mb-1`}
                    >
                      <div className={`max-w-[84%] flex flex-col gap-0.5 ${isMe ? "items-end" : "items-start"}`}>
                        {!isMe && (
                          <span className="text-[12px] text-slate-500 px-2 font-bold">
                            {msg.senderName} ({t(`roles.${msg.senderRole}`)})
                          </span>
                        )}
                        <div className={`rounded-3xl px-4 py-3 text-base leading-relaxed shadow-sm ${
                          isMe
                            ? "bg-[#2C5E53] text-white rounded-br-lg"
                            : "bg-[#F3F1EC] text-slate-800 rounded-bl-lg border border-[#2C5E53]/10"
                        }`}>
                          {msg.type === "file" && msg.file ? (
                            isImageFile(msg.file) ? (
                              <div className="space-y-2 w-full max-w-[17rem] sm:max-w-sm">
                                <a
                                  href={getMessageFileUrl(msg.file)}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  data-testid={`message-image-preview-${msg.id}`}
                                  className="block aspect-[4/3] max-h-72 min-h-40 overflow-hidden rounded-2xl bg-white/20 ring-1 ring-white/20"
                                  title={t("messages.viewPhoto")}
                                >
                                  <img
                                    src={getMessageFileUrl(msg.file)}
                                    alt={msg.file.name || t("messages.photoAlt")}
                                    className="h-full w-full object-contain"
                                    loading="lazy"
                                  />
                                </a>
                                {msg.content && msg.content !== msg.file.name && (
                                  <p className="text-sm leading-relaxed" data-testid={`message-file-caption-${msg.id}`}>
                                    {msg.content}
                                  </p>
                                )}
                                <div className={`flex items-center justify-between gap-3 text-xs ${isMe ? "text-white/80" : "text-slate-500"}`}>
                                  <span className="truncate">{msg.file.name}</span>
                                  <a
                                    href={getMessageFileUrl(msg.file)}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    data-testid={`message-file-download-${msg.id}`}
                                    className={`p-2 rounded-2xl flex-shrink-0 ${isMe ? "hover:bg-white/20" : "hover:bg-sky-50"} transition-colors`}
                                    title={t("download")}
                                  >
                                    <Download className="w-4 h-4" strokeWidth={1.5} />
                                  </a>
                                </div>
                              </div>
                            ) : (
                              <div className="flex items-center gap-2.5">
                                <div className={`w-10 h-10 rounded-2xl flex items-center justify-center flex-shrink-0 ${isMe ? "bg-white/20" : "bg-sky-50"}`}>
                                  <FileText className="w-5 h-5" strokeWidth={1.5} />
                                </div>
                                <div className="min-w-0 flex-1">
                                  <p className="text-sm font-medium truncate">{msg.file.name}</p>
                                  <p className={`text-xs ${isMe ? "text-white/70" : "text-slate-400"}`}>
                                    {(msg.file.size / 1024).toFixed(0)} Ko
                                  </p>
                                </div>
                                <a
                                  href={getMessageFileUrl(msg.file)}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  data-testid={`message-file-download-${msg.id}`}
                                  className={`p-2 rounded-2xl flex-shrink-0 ${isMe ? "hover:bg-white/20" : "hover:bg-sky-50"} transition-colors`}
                                >
                                  <Download className="w-4 h-4" strokeWidth={1.5} />
                                </a>
                              </div>
                            )
                          ) : (
                            msg.content
                          )}
                        </div>
                        <span className="text-[11px] text-slate-400 px-2 font-semibold">
                          {new Date(msg.createdAt).toLocaleTimeString(language === "ar" ? "ar-MA" : "fr-FR", { hour: "2-digit", minute: "2-digit" })}
                        </span>
                      </div>
                    </div>
                  </div>
                );
              })}

              {peerTyping && (
                <div className="flex justify-start mb-1">
                  <div className="bg-white rounded-3xl rounded-bl-lg px-4 py-3 flex gap-1 border border-sky-50 shadow-sm" data-testid="patient-peer-typing-indicator">
                    <span className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: "0ms" }} />
                    <span className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: "150ms" }} />
                    <span className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: "300ms" }} />
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>

            <div className="px-3 py-2.5 border-t border-[#2C5E53]/10 bg-white" data-testid="patient-message-guidance-panel">
              <div className="grid grid-cols-4 gap-1.5" data-testid="patient-message-intent-list">
                {intentOptions.map((intent) => (
                  <button
                    key={intent.key}
                    type="button"
                    onClick={() => applyIntentTemplate(intent.template)}
                    disabled={!selectedThread}
                    data-testid={`patient-message-intent-${intent.key}-button`}
                    className="min-w-0 rounded-full bg-[#E9F0EE] text-[#2C5E53] border border-[#2C5E53]/10 px-1.5 py-2 text-[11px] font-extrabold hover:bg-[#dfe9e5] disabled:opacity-40 transition-[background-color,color,border-color] duration-200 truncate"
                  >
                    {intent.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Input */}
            <form onSubmit={sendMessage} className="p-3 border-t border-[#2C5E53]/10 flex gap-2.5 items-center bg-white">
              <input type="file" ref={fileInputRef} onChange={handleFileSelect} className="hidden" accept="image/*,.pdf,.doc,.docx" data-testid="patient-file-input" />
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading || !selectedThread}
                className="h-12 w-12 text-slate-400 hover:bg-[#E9F0EE] hover:text-[#2C5E53] rounded-2xl transition-all duration-200 disabled:opacity-40 flex-shrink-0 flex items-center justify-center"
                title={t("messages.attachFile")}
                data-testid="patient-attach-file-btn"
              >
                {uploading ? (
                  <div className="w-5 h-5 border-2 border-slate-400 border-t-transparent rounded-full animate-spin" />
                ) : (
                  <Paperclip className="w-5 h-5" strokeWidth={1.5} />
                )}
              </button>
              <input
                type="text"
                ref={messageInputRef}
                value={newMsg}
                onChange={e => setNewMsg(e.target.value)}
                data-testid="patient-message-input"
                placeholder={t("messages.placeholder")}
                className="flex-1 h-12 px-4 rounded-2xl border border-[#2C5E53]/10 text-base focus:outline-none focus:ring-4 focus:ring-[#E9F0EE] transition-shadow bg-[#FCFBF8] placeholder:text-slate-400"
              />
              <button
                type="submit"
                disabled={sending || !newMsg.trim()}
                data-testid="patient-send-btn"
                className="h-12 w-12 bg-[#2C5E53] text-white rounded-2xl flex items-center justify-center hover:bg-[#244d44] disabled:opacity-40 transition-all duration-200 shadow-sm"
              >
                <Send className="w-5 h-5" />
              </button>
            </form>
          </div>
        )}
      </div>
    </PatientLayout>
  );
}
